/*
 * The document model of a chat message: the shared node and mark specs of `core/editor/dialect/`
 * with the five answers a message gives differently. Every node and mark here has a counterpart in
 * the server's node registry (core/render/nodes.ts) and in the markdown the server parses, and
 * nothing else may be typed, pasted or dropped into a message.
 *
 * The differences, each with its reason:
 *
 *   heading      drawn `h{level+2}`: the view owns h1 and h2, so a message heading starts at h3.
 *   code_block   only a language the server's own pattern allows reaches `params`.
 *   list_item    `paragraph block*`, the shape the server's renderer expects of a message.
 *   code         excludes every other mark, exactly as the server never puts one inside a code span.
 *   mark order   em, strong, strike, code, link — link innermost, as the server writes them.
 */

import type { MarkSpec, NodeSpec } from "prosemirror-model"
import { baseMarks, baseNodes, decorativeTaskBox, hrefChecker } from "../dialect/index.ts"

/**
 * The info string a fence may carry, character for character the server's langPattern. The source
 * is exported because the input rule needs the same characters inside a larger pattern: the
 * dialect is configured here and nowhere else.
 */
export const LANG_SOURCE = "[a-z0-9+#.-]{0,24}"
const LANG = new RegExp(`^${LANG_SOURCE}$`)

/**
 * Whether a destination may become a link, an image or an anchor target. It mirrors the server's
 * LinkHref (internal/core/markdown/mdtext/href.go) exactly — the same refusals, the same
 * allow-list — because a link the composer draws that the server will not draw is a lie about what
 * was sent. A destination without a scheme is an absolute in-app path or a fragment and nothing
 * else: a message is read where the repository is not. parseHtml.test.ts pins this against the same
 * list of destinations as Go's TestLinkSchemes.
 */
export const allowedHref = hrefChecker({
	schemes: ["http", "https", "mailto"],
	pathRule: "ownOrigin",
})

/** The heading level a message draws: the view owns h1 and h2, so a message heading starts at h3. */
function headingTag(level: number): string {
	return `h${Math.min(Math.max(level, 1) + 2, 6)}`
}

export const messageNodes: Record<string, NodeSpec> = baseNodes({
	headingTag,
	allowedHref,
	codeBlockParams: (raw) => {
		const params = raw.toLowerCase()
		return LANG.test(params) ? params : ""
	},
	listItemContent: "paragraph block*",
	taskBox: decorativeTaskBox,
})

/** Outermost first; a link inside a code span is what the server's own markdown writes. */
const shared = baseMarks({ codeExcludes: "_", allowedHref })

export const messageMarks: Record<string, MarkSpec> = {
	em: shared.em,
	strong: shared.strong,
	strike: shared.strike,
	code: shared.code,
	link: shared.link,
}
